import React, { useState, useEffect } from 'react';
import { 
  ClockIcon, 
  DocumentArrowDownIcon, 
  PlayIcon, 
  StopIcon, 
  CogIcon,
  TrashIcon,
  DocumentTextIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';
import { dailyBackupService } from '../services/api';

interface BackupInfo {
  date: string;
  path: string;
  info: {
    timestamp: string;
    files: Record<string, any>;
    summary: any;
  } | null;
}

interface SchedulerStatus {
  isRunning: boolean;
  scheduledTime: string;
  nextBackupCheck: string;
}

interface BackupStats {
  totalBackups: number;
  latestBackup: BackupInfo | null;
  oldestBackup: BackupInfo | null;
  totalSize: number;
  averageSize: number;
}

const BackupManagement: React.FC = () => {
  const [backups, setBackups] = useState<BackupInfo[]>([]);
  const [schedulerStatus, setSchedulerStatus] = useState<SchedulerStatus | null>(null);
  const [stats, setStats] = useState<BackupStats | null>(null);
  const [summary, setSummary] = useState<any>(null);
  const [incrementalSummary, setIncrementalSummary] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [newTime, setNewTime] = useState('');
  const [showTimeModal, setShowTimeModal] = useState(false);
  const [showIncrementalSummary, setShowIncrementalSummary] = useState(false);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadBackups(),
        loadSchedulerStatus(),
        loadStats(),
        loadSummary(),
        loadIncrementalSummary()
      ]);
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadBackups = async () => {
    try {
      const data = await dailyBackupService.getBackupList();
      if (data.success) {
        setBackups(data.data);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des sauvegardes:', error);
    }
  };

  const loadSchedulerStatus = async () => {
    try {
      const data = await dailyBackupService.getSchedulerStatus();
      if (data.success) {
        setSchedulerStatus(data.data);
        setNewTime(data.data.scheduledTime);
      }
    } catch (error) {
      console.error('Erreur lors du chargement du statut:', error);
    }
  };

  const loadStats = async () => {
    try {
      const data = await dailyBackupService.getBackupStats();
      if (data.success) {
        setStats(data.data);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des statistiques:', error);
    }
  };

  const loadSummary = async () => {
    try {
      const data = await dailyBackupService.getModificationSummary();
      if (data.success) {
        setSummary(data.data);
      }
    } catch (error) {
      console.error('Erreur lors du chargement du résumé:', error);
    }
  };

  const loadIncrementalSummary = async () => {
    try {
      const data = await dailyBackupService.getIncrementalSummary();
      if (data.success) {
        setIncrementalSummary(data.data);
      }
    } catch (error) {
      console.error('Erreur lors du chargement du résumé incrémental:', error);
    }
  };

  const performBackup = async () => {
    setLoading(true);
    try {
      const data = await dailyBackupService.performBackup();
      
      if (data.success) {
        alert('Sauvegarde effectuée avec succès !');
        await loadData();
      } else {
        alert(`Erreur: ${data.message}`);
      }
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      alert('Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  const performIncrementalBackup = async () => {
    setLoading(true);
    try {
      const data = await dailyBackupService.performIncrementalBackup();
      
      if (data.success) {
        alert('Sauvegarde incrémentale effectuée avec succès !');
        await loadData();
        await loadIncrementalSummary();
      } else {
        alert(`Erreur: ${data.message}`);
      }
    } catch (error) {
      console.error('Erreur lors de la sauvegarde incrémentale:', error);
      alert('Erreur lors de la sauvegarde incrémentale');
    } finally {
      setLoading(false);
    }
  };

  const forceBackup = async () => {
    setLoading(true);
    try {
      const data = await dailyBackupService.forceBackup();
      
      if (data.success) {
        alert('Sauvegarde forcée effectuée avec succès !');
        await loadData();
      } else {
        alert(`Erreur: ${data.message}`);
      }
    } catch (error) {
      console.error('Erreur lors de la sauvegarde forcée:', error);
      alert('Erreur lors de la sauvegarde forcée');
    } finally {
      setLoading(false);
    }
  };

  const toggleScheduler = async () => {
    if (!schedulerStatus) return;
    
    setLoading(true);
    try {
      const data = schedulerStatus.isRunning 
        ? await dailyBackupService.stopScheduler()
        : await dailyBackupService.startScheduler();
      
      if (data.success) {
        await loadSchedulerStatus();
      } else {
        alert(`Erreur: ${data.message}`);
      }
    } catch (error) {
      console.error('Erreur lors du changement d\'état du planificateur:', error);
      alert('Erreur lors du changement d\'état du planificateur');
    } finally {
      setLoading(false);
    }
  };

  const updateSchedulerTime = async () => {
    setLoading(true);
    try {
      const data = await dailyBackupService.setSchedulerTime(newTime);
      
      if (data.success) {
        setShowTimeModal(false);
        await loadSchedulerStatus();
        alert('Heure mise à jour avec succès !');
      } else {
        alert(`Erreur: ${data.message}`);
      }
    } catch (error) {
      console.error('Erreur lors de la mise à jour de l\'heure:', error);
      alert('Erreur lors de la mise à jour de l\'heure');
    } finally {
      setLoading(false);
    }
  };

  const cleanOldBackups = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir nettoyer les anciennes sauvegardes (> 30 jours) ?')) {
      return;
    }

    setLoading(true);
    try {
      const data = await dailyBackupService.cleanOldBackups(30);
      
      if (data.success) {
        alert(data.message);
        await loadData();
      } else {
        alert(`Erreur: ${data.message}`);
      }
    } catch (error) {
      console.error('Erreur lors du nettoyage:', error);
      alert('Erreur lors du nettoyage');
    } finally {
      setLoading(false);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('fr-FR');
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Gestion des Sauvegardes</h1>
        <div className="flex space-x-2">
          <button
            onClick={performBackup}
            disabled={loading}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-2"
          >
            <DocumentArrowDownIcon className="h-5 w-5" />
            <span>Sauvegarder maintenant</span>
          </button>
          <button
            onClick={performIncrementalBackup}
            disabled={loading}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center space-x-2"
          >
            <DocumentArrowDownIcon className="h-5 w-5" />
            <span>Sauvegarde incrémentale</span>
          </button>
          <button
            onClick={forceBackup}
            disabled={loading}
            className="bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 disabled:opacity-50 flex items-center space-x-2"
          >
            <PlayIcon className="h-5 w-5" />
            <span>Forcer sauvegarde</span>
          </button>
        </div>
      </div>

      {/* Statut du planificateur */}
      {schedulerStatus && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center text-gray-900 dark:text-white">
            <ClockIcon className="h-5 w-5 mr-2" />
            Planificateur de Sauvegarde
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Statut</p>
              <p className={`font-semibold ${schedulerStatus.isRunning ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                {schedulerStatus.isRunning ? '🟢 Actif' : '🔴 Arrêté'}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Heure programmée</p>
              <p className="font-semibold text-gray-900 dark:text-white">{schedulerStatus.scheduledTime}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Prochaine vérification</p>
              <p className="font-semibold text-sm text-gray-900 dark:text-white">{formatDate(schedulerStatus.nextBackupCheck)}</p>
            </div>
          </div>
          <div className="mt-4 flex space-x-2">
            <button
              onClick={toggleScheduler}
              disabled={loading}
              className={`px-4 py-2 rounded-lg text-white disabled:opacity-50 flex items-center space-x-2 ${
                schedulerStatus.isRunning 
                  ? 'bg-red-600 hover:bg-red-700' 
                  : 'bg-green-600 hover:bg-green-700'
              }`}
            >
              {schedulerStatus.isRunning ? <StopIcon className="h-4 w-4" /> : <PlayIcon className="h-4 w-4" />}
              <span>{schedulerStatus.isRunning ? 'Arrêter' : 'Démarrer'}</span>
            </button>
            <button
              onClick={() => setShowTimeModal(true)}
              className="bg-gray-600 dark:bg-gray-700 text-white px-4 py-2 rounded-lg hover:bg-gray-700 dark:hover:bg-gray-600 flex items-center space-x-2"
            >
              <CogIcon className="h-4 w-4" />
              <span>Modifier l'heure</span>
            </button>
          </div>
        </div>
      )}

      {/* Statistiques */}
      {stats && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center text-gray-900 dark:text-white">
            <ChartBarIcon className="h-5 w-5 mr-2" />
            Statistiques
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Total sauvegardes</p>
              <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{stats.totalBackups}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Dernière sauvegarde</p>
              <p className="font-semibold text-gray-900 dark:text-white">
                {stats.latestBackup ? stats.latestBackup.date : 'Aucune'}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Taille totale</p>
              <p className="font-semibold text-gray-900 dark:text-white">{formatFileSize(stats.totalSize)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Taille moyenne</p>
              <p className="font-semibold text-gray-900 dark:text-white">{formatFileSize(stats.averageSize)}</p>
            </div>
          </div>
        </div>
      )}

      {/* Résumé des modifications */}
      {summary && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold flex items-center text-gray-900 dark:text-white">
              <DocumentTextIcon className="h-5 w-5 mr-2" />
              {showIncrementalSummary ? 'Résumé Incrémental' : 'Résumé Quotidien'}
            </h2>
            <div className="flex space-x-2">
              <button
                onClick={() => setShowIncrementalSummary(false)}
                className={`px-3 py-1 rounded text-sm ${
                  !showIncrementalSummary 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                }`}
              >
                Quotidien
              </button>
              <button
                onClick={() => setShowIncrementalSummary(true)}
                className={`px-3 py-1 rounded text-sm ${
                  showIncrementalSummary 
                    ? 'bg-green-600 text-white' 
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                }`}
              >
                Incrémental
              </button>
            </div>
          </div>
          
          {showIncrementalSummary && incrementalSummary ? (
            // Affichage du résumé incrémental
            incrementalSummary.type === 'initial_backup' ? (
              <div className="text-center py-4">
                <p className="text-gray-600 dark:text-gray-400">🎯 Première sauvegarde</p>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">{incrementalSummary.message}</p>
              </div>
            ) : (
              <div>
                {incrementalSummary.comparison_with && (
                  <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900 rounded-lg">
                    <p className="text-sm text-blue-800 dark:text-blue-200">
                      📊 Comparé avec: {new Date(incrementalSummary.comparison_with).toLocaleString('fr-FR')}
                    </p>
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  {incrementalSummary.websites && (
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Sites Web</p>
                      <p className="font-semibold text-gray-900 dark:text-white">
                        {incrementalSummary.websites.total} total
                        {incrementalSummary.websites.added > 0 && <span className="text-green-600 dark:text-green-400"> (+{incrementalSummary.websites.added})</span>}
                        {incrementalSummary.websites.removed > 0 && <span className="text-red-600 dark:text-red-400"> (-{incrementalSummary.websites.removed})</span>}
                        {incrementalSummary.websites.modified > 0 && <span className="text-orange-600 dark:text-orange-400"> (✏️{incrementalSummary.websites.modified})</span>}
                      </p>
                      {incrementalSummary.websites.details?.modified && incrementalSummary.websites.details.modified.length > 0 && (
                        <div className="mt-2 text-xs">
                          <p className="font-medium text-orange-600 dark:text-orange-400">Modifications détaillées:</p>
                          {incrementalSummary.websites.details.modified.slice(0, 3).map((site: any, index: number) => (
                            <div key={index} className="mt-1 p-2 bg-orange-50 dark:bg-orange-900 rounded">
                              <p className="font-medium text-gray-900 dark:text-white">{site.clientName}</p>
                              <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">{site.url}</p>
                              {site.changes && site.changes.slice(0, 2).map((change: any, changeIndex: number) => (
                                <p key={changeIndex} className="text-xs text-gray-700 dark:text-gray-300">
                                  • <span className="font-medium">{change.field}</span>: 
                                  <span className="text-red-600 dark:text-red-400"> "{change.oldValue}"</span> → 
                                  <span className="text-green-600 dark:text-green-400"> "{change.newValue}"</span>
                                </p>
                              ))}
                            </div>
                          ))}
                          {incrementalSummary.websites.details.modified.length > 3 && (
                            <p className="text-gray-500 dark:text-gray-400 mt-1">
                              ... et {incrementalSummary.websites.details.modified.length - 3} autres modifications
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {incrementalSummary.users && (
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Utilisateurs</p>
                      <p className="font-semibold text-gray-900 dark:text-white">
                        {incrementalSummary.users.total} total
                        {incrementalSummary.users.added > 0 && <span className="text-green-600 dark:text-green-400"> (+{incrementalSummary.users.added})</span>}
                        {incrementalSummary.users.removed > 0 && <span className="text-red-600 dark:text-red-400"> (-{incrementalSummary.users.removed})</span>}
                        {incrementalSummary.users.modified > 0 && <span className="text-orange-600 dark:text-orange-400"> (✏️{incrementalSummary.users.modified})</span>}
                      </p>
                    </div>
                  )}
                  {incrementalSummary.sessions && (
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Sessions actives</p>
                      <p className="font-semibold text-gray-900 dark:text-white">{incrementalSummary.sessions.active_sessions}</p>
                    </div>
                  )}
                  {incrementalSummary.locks && (
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Verrous actifs</p>
                      <p className="font-semibold text-gray-900 dark:text-white">{incrementalSummary.locks.active_locks}</p>
                    </div>
                  )}
                </div>
              </div>
            )
          ) : !showIncrementalSummary ? (
            // Affichage du résumé quotidien existant
            summary.type === 'initial_backup' ? (
              <div className="text-center py-4">
                <p className="text-gray-600 dark:text-gray-400">🎯 Première sauvegarde</p>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">{summary.message}</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {summary.websites && (
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Sites Web</p>
                    <p className="font-semibold text-gray-900 dark:text-white">
                      {summary.websites.total} total
                      {summary.websites.added > 0 && <span className="text-green-600 dark:text-green-400"> (+{summary.websites.added})</span>}
                      {summary.websites.removed > 0 && <span className="text-red-600 dark:text-red-400"> (-{summary.websites.removed})</span>}
                      {summary.websites.modified > 0 && <span className="text-orange-600 dark:text-orange-400"> (✏️{summary.websites.modified})</span>}
                    </p>
                  </div>
                )}
                
                {summary.users && (
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Utilisateurs</p>
                    <p className="font-semibold text-gray-900 dark:text-white">
                      {summary.users.total} total
                      {summary.users.added > 0 && <span className="text-green-600 dark:text-green-400"> (+{summary.users.added})</span>}
                      {summary.users.removed > 0 && <span className="text-red-600 dark:text-red-400"> (-{summary.users.removed})</span>}
                      {summary.users.modified > 0 && <span className="text-orange-600 dark:text-orange-400"> (✏️{summary.users.modified})</span>}
                    </p>
                  </div>
                )}
                {summary.sessions && (
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Sessions actives</p>
                    <p className="font-semibold text-gray-900 dark:text-white">{summary.sessions.active_sessions}</p>
                  </div>
                )}
                {summary.locks && (
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Verrous actifs</p>
                    <p className="font-semibold text-gray-900 dark:text-white">{summary.locks.active_locks}</p>
                  </div>
                )}
              </div>
            )
          ) : (
            <div className="text-center py-4">
              <p className="text-gray-600 dark:text-gray-400">Aucun résumé incrémental disponible</p>
              <button
                onClick={loadIncrementalSummary}
                className="mt-2 text-blue-600 dark:text-blue-400 hover:underline"
              >
                Actualiser
              </button>
            </div>
          )}
        </div>
      )}

      {/* Liste des sauvegardes */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Historique des Sauvegardes</h2>
          <button
            onClick={cleanOldBackups}
            disabled={loading}
            className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center space-x-2"
          >
            <TrashIcon className="h-4 w-4" />
            <span>Nettoyer anciennes</span>
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Heure
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Fichiers
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Taille
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Statut
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {backups.map((backup) => (
                <tr key={backup.date} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                    {backup.date}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {backup.info ? formatDate(backup.info.timestamp) : 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {backup.info ? Object.keys(backup.info.files).length : 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {backup.info ? formatFileSize(
                      Object.values(backup.info.files).reduce((total: number, file: any) => 
                        total + (file.size || 0), 0)
                    ) : 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {backup.info ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200">
                        ✅ Réussie
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200">
                        ⚠️ Info manquante
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {backups.length === 0 && (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              Aucune sauvegarde trouvée
            </div>
          )}
        </div>
      </div>

      {/* Modal pour modifier l'heure */}
      {showTimeModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border border-gray-300 dark:border-gray-600 w-96 shadow-lg rounded-md bg-white dark:bg-gray-800">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                Modifier l'heure de sauvegarde
              </h3>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Nouvelle heure (HH:MM)
                </label>
                <input
                  type="time"
                  value={newTime}
                  onChange={(e) => setNewTime(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              <div className="flex justify-end space-x-2">
                <button
                  onClick={() => setShowTimeModal(false)}
                  className="px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-400 dark:hover:bg-gray-500"
                >
                  Annuler
                </button>
                <button
                  onClick={updateSchedulerTime}
                  disabled={loading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  Sauvegarder
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BackupManagement; 